import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-sm-5 text-[13px] font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'shadow-[0_2px_2px_0_rgba(0,0,0,0.25)] bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'shadow-[0_2px_2px_0_rgba(0,0,0,0.25)] border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        'outline-light':
          'shadow-[0_2px_2px_0_rgba(0,0,0,0.25)] border border-input bg-light hover:bg-[#eaf1f1] hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        green:
          'shadow-[0_2px_2px_0_rgba(1,112,69,0.45)] gap-2 border border-[#33FF71] bg-[#33FF7180] text-ebony hover:bg-[#33FF71]',
        'bright-green':
          'shadow-[0_2px_2px_0_rgba(1,159,75,0.25)] gap-2 border border-[#33FF71] bg-[#33FF71B2] text-ebony hover:bg-[#33FF71]',
        purple:
          'shadow-[0_2px_2px_0_rgba(122,107,229,0.60)] gap-2 border border-[#A498FF4D] bg-[#A498FF80] text-ebony hover:bg-[#7c6cea99]',
        red: 'shadow-[0_1px_2px_0_rgba(229,32,32,0.45)] gap-2 border border-red_750 bg-red_600 text-white hover:bg-red_750 hover:border-red_900',
        blue: 'shadow-[0_1px_1px_0_rgba(7,75,84,0.20)] gap-2 border border-[#009CB226] bg-[#009CB212] text-[#009CB2] hover:bg-[#009cb238]',
        tertiary:
          'shadow-[0_1px_1px_0_rgba(5,5,13,0.25)] gap-2 rounded-lg border border-grey_300 bg-white font-semibold text-ebony hover:bg-light',
        dark: 'shadow-[0_1px_1px_0_rgba(5,5,13,0.25)] gap-2 rounded-lg bg-ebony font-semibold text-white hover:bg-ebony_opacity_80 disabled:bg-grey_300 disabled:opacity-100 disabled:shadow-none',
        'ghost-grey': 'gap-2 rounded-lg font-semibold text-tertiary hover:bg-light',
        'ghost-accent': 'gap-2 rounded-lg font-semibold text-green_750 hover:bg-green_150',
        // Pill buttons (use with size "pill"): the original app button styles.
        'pill-outline': 'border border-black bg-transparent text-ebony',
        'pill-outline-dark':
          'border-2 border-ebony bg-transparent text-ebony hover:bg-[var(--grey-hover-color)]',
        'pill-outline-red':
          'gap-3 border-2 border-[var(--red-color)] bg-transparent text-[var(--red-color)] hover:bg-[#FFF1F1]',
        'pill-blue': 'gap-3 bg-ebony text-malibu hover:bg-[var(--dark-hover-color)]',
        'pill-green': 'gap-3 bg-ebony text-green-default hover:bg-[var(--dark-hover-color)]',
        'pill-yellow':
          'gap-3 bg-ebony text-[var(--yellow-color)] hover:bg-[var(--dark-hover-color)]',
        'pill-purple':
          'gap-3 bg-ebony text-[var(--purple-color)] hover:bg-[var(--dark-hover-color)]',
      },
      size: {
        default: 'h-8.5 px-3.5 py-2',
        sm: 'h-9 rounded-sm-5 px-3',
        lg: 'h-11 rounded-sm-5 px-8',
        xl: 'h-[50px] rounded-sm-5 px-8',
        icon: 'h-10 w-10',
        // Full-width pill with the original app button's type, spacing and states.
        // tailwind-merge doesn't know the custom rounded-sm-5 radius in the base classes, so the
        // pill radius is marked important to win over it.
        pill: "mt-2.5 flex h-[31px] w-full !rounded-full p-2.5 [font-family:'Bai_Jamjuree',Onest] text-sm font-semibold leading-[normal] transition-all duration-100 ease-linear active:shadow-[inset_2px_1px_3px_rgba(0,0,0,0.1)] disabled:cursor-default disabled:opacity-30 md:h-[45px] md:px-5",
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
